import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { EmployeeService } from 'src/app/services/employee.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';

declare const $: any;

// Catálogo SAT c_TipoRegimen (CFDI Nómina)
export const SAT_TAX_REGIMES: string[] = [
    '02 - Sueldos',
    '03 - Jubilados',
    '04 - Pensionados',
    '05 - Asimilados Miembros Sociedades Cooperativas Producción',
    '06 - Asimilados Miembros Consejos',
    '07 - Asimilados Comisionistas',
    '08 - Asimilados Honorarios',
    '09 - Asimilados Acciones',
    '10 - Asimilados Otros',
    '13 - Otro Régimen'
];

// Catálogo SAT c_TipoContrato
export const SAT_CONTRACT_TYPES: string[] = [
    '01 - Contrato de trabajo por tiempo indeterminado',
    '02 - Contrato de trabajo para obra determinada',
    '03 - Contrato de trabajo por tiempo determinado',
    '04 - Contrato de trabajo por temporada',
    '05 - Contrato de trabajo sujeto a prueba',
    '06 - Contrato de trabajo con capacitación inicial',
    '07 - Modalidad de contratación por pago de hora laborada',
    '08 - Modalidad de subcontratación laboral',
    '09 - Otro contrato'
];

// Catálogo SAT c_TipoJornada
export const SAT_WORK_SCHEDULE_TYPES: string[] = [
    '01 - Diurna',
    '02 - Nocturna',
    '03 - Mixta',
    '04 - Por hora',
    '05 - Reducida',
    '06 - Continuada',
    '07 - Partida',
    '08 - Por turnos',
    '99 - Otra jornada'
];

// Catálogo SAT c_Estado (código - nombre)
export const SAT_FEDERAL_ENTITIES: string[] = [
    'AGU - Aguascalientes', 'BCN - Baja California', 'BCS - Baja California Sur',
    'CAM - Campeche', 'CHP - Chiapas', 'CHH - Chihuahua', 'CMX - Ciudad de México',
    'COA - Coahuila', 'COL - Colima', 'DUR - Durango', 'GUA - Guanajuato',
    'GRO - Guerrero', 'HID - Hidalgo', 'JAL - Jalisco', 'MEX - Estado de México',
    'MIC - Michoacán', 'MOR - Morelos', 'NAY - Nayarit', 'NLE - Nuevo León',
    'OAX - Oaxaca', 'PUE - Puebla', 'QUE - Querétaro', 'ROO - Quintana Roo',
    'SLP - San Luis Potosí', 'SIN - Sinaloa', 'SON - Sonora', 'TAB - Tabasco',
    'TAM - Tamaulipas', 'TLA - Tlaxcala', 'VER - Veracruz', 'YUC - Yucatán',
    'ZAC - Zacatecas'
];

@Component({
    selector: 'app-fiscal-tab',
    templateUrl: './fiscal-tab.component.html',
    styleUrls: ['./fiscal-tab.component.css', '../employee-tab-shared.css']
})
export class FiscalTabComponent implements OnInit {
    @Input() employee: any;
    @Output() saved = new EventEmitter<any>();

    form!: FormGroup;
    regimeDetailsDraftForm!: FormGroup;
    readonly maxTaxRegimeDetailsLength = 200;

    states = SAT_FEDERAL_ENTITIES;
    taxRegimes = SAT_TAX_REGIMES;
    contractTypes = SAT_CONTRACT_TYPES;
    workScheduleTypes = SAT_WORK_SCHEDULE_TYPES;
    employeeTypes = ['Confianza', 'Sindicalizado', 'Base', 'Eventual', 'Honorarios'];
    incomeTypeOptions = ['Asalariado', 'Asimilables a salarios', 'Actividades empresariales asimilables a salarios'];

    constructor(
        private fb: FormBuilder,
        private employeeService: EmployeeService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Listado de colaboradores', action);
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            tax_regime: [this.employee?.tax_regime || ''],
            tax_regime_details: [this.employee?.tax_regime_details || '', Validators.maxLength(this.maxTaxRegimeDetailsLength)],
            income_type: [this.employee?.income_type || ''],
            taxable_isr: [this.employee?.taxable_isr ?? true],
            taxable_imss: [this.employee?.taxable_imss ?? true],
            taxable_employment_subsidy: [this.employee?.taxable_employment_subsidy ?? true],
            estimated_annual_income_hire: [this.employee?.estimated_annual_income_hire ?? null],
            estimated_annual_income_termination: [this.employee?.estimated_annual_income_termination ?? null],
            service_federal_entity: [this.employee?.service_federal_entity || ''],
            contract_type: [this.employee?.contract_type || ''],
            work_schedule_type: [this.employee?.work_schedule_type || ''],
            employee_type: [this.employee?.employee_type || ''],
            custom_template: [this.employee?.custom_template || '']
        });

        this.regimeDetailsDraftForm = this.fb.group({
            income_type: [''],
            taxable_isr: [true],
            taxable_imss: [true],
            taxable_employment_subsidy: [true],
            estimated_annual_income_hire: [null],
            estimated_annual_income_termination: [null]
        });
    }

    setIncomeType(option: string): void {
        this.regimeDetailsDraftForm.get('income_type')?.setValue(option);
    }

    openRegimeDetailsModal(): void {
        this.regimeDetailsDraftForm.patchValue({
            income_type: this.form.value.income_type,
            taxable_isr: this.form.value.taxable_isr,
            taxable_imss: this.form.value.taxable_imss,
            taxable_employment_subsidy: this.form.value.taxable_employment_subsidy,
            estimated_annual_income_hire: this.form.value.estimated_annual_income_hire,
            estimated_annual_income_termination: this.form.value.estimated_annual_income_termination
        });
        $('#regimeDetailsModal').modal('show');
    }

    saveRegimeDetails(): void {
        this.form.patchValue(this.regimeDetailsDraftForm.value);
        $('#regimeDetailsModal').modal('hide');
    }

    save(): void {
        const payload = this.form.value;

        this.spinner.show();
        this.employeeService.update(this.employee.id, payload).subscribe({
            next: (resp: any) => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'La información fiscal se guardó correctamente', icon: 'success' });
                this.saved.emit(resp);
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo guardar la información', icon: 'error' });
            }
        });
    }
}
